/**
 * Constantes du catalogue public (ressources.lde.fr) liées au pays choisi par le visiteur.
 * Doivent rester synchronisées avec backend/catalogues/catalogue_public.py.
 */

export const PAYS_FRANCE = "france";
export const PAYS_SUISSE = "suisse";
export const PAYS_BELGIQUE = "belgique";
export const PAYS_CANADA = "canada";
export const PAYS_AUTRE = "autre";

/**
 * Pays proposés au visiteur, dans l'ordre d'affichage.
 */
export const PAYS_CATALOGUE_PUBLIC = [PAYS_FRANCE, PAYS_SUISSE, PAYS_BELGIQUE, PAYS_CANADA, PAYS_AUTRE];

/**
 * Pays dont le parcours de commande passe par la création manuelle de l'établissement (pas d'API gouv).
 */
export const PAYS_EXPORT = [PAYS_SUISSE, PAYS_BELGIQUE, PAYS_CANADA];

/**
 * Pays disposant d'un catalogue et d'un parcours de demande en ligne.
 */
export const PAYS_AVEC_CATALOGUE = [PAYS_FRANCE, ...PAYS_EXPORT];

/**
 * Indique si le pays relève du parcours Export.
 * @param {String} pays Code du pays.
 * @returns {Boolean}
 */
export function isPaysExport(pays) {
  return PAYS_EXPORT.includes(pays);
}

/**
 * Options du select de choix du pays.
 * @param {Function} t Fonction de traduction.
 * @returns {Array} Options { id, label, value }.
 */
export function getPaysOptions(t) {
  return PAYS_CATALOGUE_PUBLIC.map((pays) => ({
    id: pays,
    label: t(`catalogue-public.pays-${pays}`),
    value: pays,
  }));
}

/**
 * Lien mailto invitant un visiteur d'un pays sans catalogue à nous contacter.
 * @param {Function} t Fonction de traduction.
 * @param {String} email Adresse de contact.
 * @returns {String} Lien mailto.
 */
export function getMailtoPaysAutre(t, email) {
  const subject = encodeURIComponent(t("catalogue-public.pays-autre-mail-subject"));
  const body = encodeURIComponent(t("catalogue-public.pays-autre-mail-body"));

  return `mailto:${email}?subject=${subject}&body=${body}`;
}

/**
 * Types d'établissement proposés dans le formulaire de création (parcours Export).
 * Les valeurs sont les natures reconnues par le CRM (SIGLE_MAP), qui en déduit le sigle :
 * elles sont envoyées telles quelles, seul le libellé traduit est affiché.
 */
export const TYPES_ETABLISSEMENT_EXPORT = [
  { value: "COLLEGE", key: "college" },
  { value: "COLLEGE CLIMATIQUE", key: "college-climatique" },
  { value: "COLLEGE EXPERIMENTAL", key: "college-experimental" },
  { value: "COLLEGE SPECIALISE", key: "college-specialise" },
  { value: "ECOLE DE NIVEAU ELEMENTAIRE", key: "ecole-elementaire" },
  { value: "ECOLE DE NIVEAU ELEMENTAIRE SPECIALISEE", key: "ecole-elementaire-specialisee" },
  { value: "ECOLE DE PLEIN AIR", key: "ecole-plein-air" },
  { value: "ECOLE ELEMENTAIRE D APPLICATION", key: "ecole-elementaire-application" },
  { value: "ECOLE MATERNELLE", key: "ecole-maternelle" },
  { value: "ECOLE MATERNELLE D APPLICATION", key: "ecole-maternelle-application" },
  { value: "ECOLE PROFESSIONNELLE SPECIALISEE", key: "ecole-professionnelle-specialisee" },
  { value: "ECOLE REGIONALE DU PREMIER DEGRE", key: "ecole-regionale-premier-degre" },
  { value: "ECOLE SECONDAIRE SPECIALISEE (2 D CYCLE)", key: "ecole-secondaire-specialisee" },
  { value: "ECOLES COMPOSEES UNIQT DE STS ET OU CPGE", key: "ecole-sts-cpge" },
  { value: "ETAB REGIONAL/LYCEE ENSEIGNEMENT ADAPTE", key: "erea" },
  { value: "ETABLISSEMENT MEDICO-EXPERIMENTAL", key: "etablissement-medico-experimental" },
  { value: "LYCEE CLIMATIQUE", key: "lycee-climatique" },
  { value: "LYCEE D ENSEIGNEMENT GENERAL", key: "lycee-general" },
  { value: "LYCEE D ENSEIGNEMENT TECHNOLOGIQUE", key: "lycee-technologique" },
  { value: "LYCEE ENS GENERAL TECHNO PROF AGRICOLE", key: "lycee-agricole" },
  { value: "LYCEE ENSEIGNT GENERAL ET TECHNOLOGIQUE", key: "lycee-general-technologique" },
  { value: "LYCEE EXPERIMENTAL", key: "lycee-experimental" },
  { value: "LYCEE POLYVALENT", key: "lycee-polyvalent" },
  { value: "LYCEE PROFESSIONNEL", key: "lycee-professionnel" },
  { value: "MAISON FAMILIALE RURALE EDUCATION ORIENT", key: "mfr" },
  { value: "SECTION D ENSEIGNEMENT PROFESSIONNEL", key: "section-enseignement-professionnel" },
  { value: "SECTION ENSEIGNT GEN. ET PROF. ADAPTE", key: "segpa" },
  { value: "SECTION ENSEIGT GENERAL ET TECHNOLOGIQUE", key: "section-general-technologique" },
];

/**
 * Options du select de type d'établissement (parcours Export).
 * @param {Function} t Fonction de traduction.
 * @returns {Array} Options { id, label, value }, value étant la nature attendue par le CRM.
 */
export function getTypeEtablissementOptions(t) {
  return TYPES_ETABLISSEMENT_EXPORT.map(({ value, key }) => ({
    id: key,
    label: t(`connexion.type-etablissement-${key}`),
    value,
  }));
}

/**
 * Libellé traduit d'un type d'établissement à partir de la nature CRM.
 * @param {Function} t Fonction de traduction.
 * @param {String} value Nature CRM (ex. "COLLEGE").
 * @returns {String} Libellé traduit (ex. "Collège"), ou la valeur brute si elle est inconnue.
 */
export function getTypeEtablissementLabel(t, value) {
  const type = TYPES_ETABLISSEMENT_EXPORT.find((item) => item.value === value);

  return type ? t(`connexion.type-etablissement-${type.key}`) : value;
}
